export interface IndicatorVariable {
    public_name: string;
    field: string;
    type: IndicatorType;
}

export interface IndicatorVariableGet extends IndicatorVariable {
    name: string;
}

export interface IndicatorBase {
    name: string;
    description?: string | null;
    formula: string;
    unit?: string | null;
    logic: Record<string, any>;
}

export interface IndicatorGet extends IndicatorBase {
    id: string;
}

export interface IndicatorCreate extends IndicatorBase { }

export interface IndicatorUpdate extends IndicatorBase { }

export interface ProductBase {
    name: string;
    description?: string | null;
    type: ProductType;
    target_segments?: string[] | null;
}

export interface ProductGet extends ProductBase {
    id: string;
}

export interface ProductCreate extends ProductBase { }

export interface ProductUpdate extends ProductBase { }

export interface SignalBase {
    name: string;
    description?: string | null;
    condition_formula: string;
    logic: Record<string, any>;
}

export interface SignalGet extends SignalBase {
    id: string;
    indicators: IndicatorGet[];
}

export interface SignalSimpleGet extends SignalBase {
    id: string;
}

export interface SignalCreate extends SignalBase {
    indicator_ids: string[];
}

export interface SignalUpdate extends SignalBase {
    indicator_ids: string[];
}

export interface OpportunityBase {
    name: string;
    description?: string | null;
    sales_prompt?: string | null;
}

export interface OpportunityGet extends OpportunityBase {
    id: string;
    product: ProductGet;
    signals: SignalGet[];
}

export interface OpportunitySimpleGet extends OpportunityBase {
    id: string;
    product: ProductGet;
    signals: SignalSimpleGet[];
}

export interface OpportunityCreate extends OpportunityBase {
    product_id: string;
    signal_ids: string[];
}

export interface OpportunityUpdate extends OpportunityBase {
    product_id: string;
    signal_ids: string[];
}

export interface CompanyWithOpportunities {
    company_id: string;
    company_name: string;
    company_segment?: string | null;
    contract_end_date?: string | null;
    opportunity_count: number;
    opportunities: OpportunityGet[];
}

export interface CompanyDashboardGet {
    companies: CompanyWithOpportunities[];
}

// Enums

export enum IndicatorType {
    DATE = 'DATE',
    STRING = 'STRING',
    NUMBER = 'NUMBER',
}

export enum ProductType {
    COMMISSION = 'COMMISSION',
}

export enum LogicType {
    VARIABLE = 'constant',
    FIELD = 'field',
    FUNCTION = 'function',
    OPERATION = 'operation',
}

export enum LogicFunction {
    TODAY = 'today',
    CURRENT_DATE = 'current_date',
    NOW = 'now',
    CURRENT_DATETIME = 'current_datetime',
}

export enum LogicOperation {
    ADD = 'add',
    SUBTRACT = 'subtract',
    MULTIPLY = 'multiply',
    DIVIDE = 'divide',
    AND = 'and',
    OR = 'or',
    NOT = 'not',
}

export enum ComparisonOperator {
    LESS_THAN = 'lt',
    GREATER_THAN = 'gt',
    LESS_THAN_OR_EQUAL = 'lte',
    GREATER_THAN_OR_EQUAL = 'gte',
    EQUAL = 'eq',
    NOT_EQUAL = 'ne',
}

// Display
export interface LogicOperationDisplay {
  label: string;
  operation: LogicOperation;
  description: string;
}

export const LOGIC_OPERATIONS: LogicOperationDisplay[] = [
  { label: '+', operation: LogicOperation.ADD, description: 'Addition' },
  { label: '-', operation: LogicOperation.SUBTRACT, description: 'Subtraction' },
  { label: '*', operation: LogicOperation.MULTIPLY, description: 'Multiplication' },
  { label: '/', operation: LogicOperation.DIVIDE, description: 'Division' },
  { label: '&&', operation: LogicOperation.AND, description: 'Logical AND' },
  { label: '||', operation: LogicOperation.OR, description: 'Logical OR' },
  { label: '!', operation: LogicOperation.NOT, description: 'Logical NOT' },
];

export interface ComparisonOperatorDisplay {
  label: string;
  operator: ComparisonOperator;
  description: string;
}

export const COMPARISON_OPERATORS: ComparisonOperatorDisplay[] = [
  { label: '<', operator: ComparisonOperator.LESS_THAN, description: 'Less than' },
  { label: '>', operator: ComparisonOperator.GREATER_THAN, description: 'Greater than' },
  { label: '<=', operator: ComparisonOperator.LESS_THAN_OR_EQUAL, description: 'Less than or equal' },
  { label: '>=', operator: ComparisonOperator.GREATER_THAN_OR_EQUAL, description: 'Greater than or equal' },
  { label: '==', operator: ComparisonOperator.EQUAL, description: 'Equal' },
  { label: '!=', operator: ComparisonOperator.NOT_EQUAL, description: 'Not equal' },
];

export interface LogicFunctionDisplay {
  label: string;
  func: LogicFunction;
  description: string;
}

export const LOGIC_FUNCTIONS: LogicFunctionDisplay[] = [
  { label: 'today()', func: LogicFunction.TODAY, description: 'Current system date' },
  { label: 'current_date()', func: LogicFunction.CURRENT_DATE, description: 'Current date' },
  { label: 'now()', func: LogicFunction.NOW, description: 'Current date & time' },
  { label: 'current_datetime()', func: LogicFunction.CURRENT_DATETIME, description: 'Current date & time (UTC)' },
];
